import { useEffect, useRef, useState, type ElementType, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Verzögerung in ms für gestaffelte Listen */
  delay?: number;
  /** Richtung der Einblend-Bewegung */
  direction?: "up" | "down" | "left" | "right" | "none";
  as?: ElementType;
}

/**
 * Blendet Inhalte beim Hereinscrollen sanft ein (einmalig).
 * Respektiert die Systemeinstellung "Bewegung reduzieren".
 */
export const Reveal = ({
  children,
  className,
  delay = 0,
  direction = "up",
  as: Tag = "div",
}: RevealProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const hidden = {
    up: "translate-y-6",
    down: "-translate-y-6",
    left: "translate-x-6",
    right: "-translate-x-6",
    none: "",
  }[direction];

  return (
    <Tag
      ref={ref as never}
      style={{ transitionDelay: `${delay}ms` }}
      className={cn(
        "transition-all duration-700 ease-out will-change-[opacity,transform] motion-reduce:transition-none motion-reduce:transform-none",
        visible ? "opacity-100 translate-x-0 translate-y-0" : cn("opacity-0", hidden),
        className
      )}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
